'use client'

import React from 'react'

import { PetCanvasSprite } from '@/components/HermesSprite'
import type { PetInfo } from '@/components/HermesSprite'

/**
 * Decorative smoking-robot sprite that perches on top of the chat
 * button (when closed) or on top of the chat panel (when open).
 * Purely visual — pointer-events are disabled so it never
 * intercepts clicks on the button or drags on the panel header
 * beneath it.
 *
 * Position is supplied by the caller via `style` (fixed left/top or
 * right/bottom offsets). MASCOT_W / MASCOT_H expose the rendered size
 * so callers can anchor it precisely.
 *
 * Z-INDEX: this is the **highest** z-index in the chat stack on
 * purpose. Hans and the chat panel live at z-40/41/42, the cart
 * Sheet at z-50, the chat lifts to z-51/52 when the cart is open.
 * The mascot is the very top — it has to perch ON TOP of the
 * button, not behind it. Earlier we wrapped the mascot in a
 * motion.div for the cart-shift animation, but transforms create
 * a new stacking context and the mascot's z-index became relative
 * to the wrapper (effectively z-0 globally), so the button at z-40
 * painted over it. Lesson: animate the `right` value on the
 * mascot itself, never wrap it. See docs/chatbot-sprite/19.
 */
const SCALE = 0.48
export const MASCOT_W = Math.round(192 * SCALE) // 92
export const MASCOT_H = Math.round(208 * SCALE) // 100

const ROBOT: PetInfo = {
  id: 'smoking-robot',
  displayName: 'Smoking Robot',
  src: '/sprites/hermes-sprites/pets/smoking-robot/spritesheet.webp',
  frameW: 192,
  frameH: 208,
  framesPerState: 6,
  loopMs: 1100,
  scale: SCALE
}

interface ChatMascotProps {
  style?: React.CSSProperties
  zIndex?: number
  onClick?: () => void
}

export const ChatMascot: React.FC<ChatMascotProps> = ({ style, zIndex = 100, onClick }) => {
  return (
    <div
      aria-hidden={!onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onClick={onClick}
      onKeyDown={
        onClick
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick()
              }
            }
          : undefined
      }
      style={{
        position: 'fixed',
        zIndex,
        lineHeight: 0,
        cursor: onClick ? 'pointer' : undefined,
        userSelect: 'none',
        filter: 'drop-shadow(0 6px 5px rgba(0, 0, 0, 0.28))',
        transition:
          'right 0.35s cubic-bezier(0.4, 0, 0.2, 1), left 0.15s ease, top 0.15s ease, transform 0.15s ease',
        ...style
      }}
      onMouseEnter={(e) => {
        if (onClick) {
          ;(e.currentTarget as HTMLDivElement).style.transform = 'scale(1.08)'
        }
      }}
      onMouseLeave={(e) => {
        if (onClick) {
          ;(e.currentTarget as HTMLDivElement).style.transform = 'scale(1)'
        }
      }}
    >
      <PetCanvasSprite info={ROBOT} state="idle" />
    </div>
  )
}

export default ChatMascot
